import { IApplication } from "@rentiohq/shared-frontend/dist/types/application.types";
import type { SortDirection } from "./ApplicationTable";
import { getApplicationIndicators } from "./ApplicationTable/applicationIndicators.utils";

/**
 * The columns of the other-applications dialog a broker can sort on: the ones
 * the candidate list sorts on, with the property standing where the candidate
 * stands there. More info is left out, as in the list: it holds several
 * unrelated counts (household, pets, move-in date, documents), so no single
 * key would say what "sorted on More info" means (DEV-3523).
 */
export type TOtherApplicationsSortColumn =
  | "property"
  | "activity"
  | "income"
  | "rating";

export interface IOtherApplicationsSortRow {
  applicationId: string;
  propertyAddress: string;
  stepCompletedAt: string | null;
  stepStartedAt: string | null;
}

/** Opens on the date in the Activity column, newest first (DEV-3523). */
export const DEFAULT_OTHER_APPLICATIONS_SORT: {
  column: TOtherApplicationsSortColumn;
  direction: SortDirection;
} = { column: "activity", direction: "desc" };

// The date the Activity column shows, so the order follows what the broker
// reads. A row without one counts as the oldest.
const getActivityTime = (row: IOtherApplicationsSortRow) => {
  const date = row.stepCompletedAt ?? row.stepStartedAt;
  const time = date ? new Date(date).getTime() : Number.NaN;
  return Number.isNaN(time) ? Number.NEGATIVE_INFINITY : time;
};

/**
 * Sorted in the dialog itself, without a request (DEV-3523). Income and rating
 * come from the applications the dialog fetches for its rows. A row whose
 * application has not arrived has no value yet, so it goes last in either
 * direction rather than passing for the lowest income or rating. The sort is
 * stable, so rows that compare equal keep the order the backend sends, which is
 * newest activity first.
 */
export const sortOtherApplications = <T extends IOtherApplicationsSortRow>(
  rows: T[],
  applicationsById: Map<string, IApplication>,
  column: TOtherApplicationsSortColumn,
  direction: SortDirection,
): T[] => {
  // Null while the row's application is still loading.
  const getFetchedValue = (row: T): number | null => {
    const application = applicationsById.get(row.applicationId);
    if (!application) {
      return null;
    }
    // A loaded application without a rating counts as zero on purpose: the row
    // draws it as zero stars and the candidate list sorts it as zero, and this
    // dialog sorts the way the list does. Only a row that has not loaded is
    // unknown.
    return column === "income"
      ? getApplicationIndicators(application).totalIncome
      : application.rating ?? 0;
  };

  if (column === "income" || column === "rating") {
    return [...rows].sort((a, b) => {
      const valueA = getFetchedValue(a);
      const valueB = getFetchedValue(b);
      if (valueA === null || valueB === null) {
        return (valueA === null ? 1 : 0) - (valueB === null ? 1 : 0);
      }
      return direction === "desc" ? valueB - valueA : valueA - valueB;
    });
  }

  const compare = (a: T, b: T) => {
    switch (column) {
      case "property":
        return a.propertyAddress.localeCompare(b.propertyAddress);
      case "activity": {
        // Compared rather than subtracted: two undated rows are both -Infinity,
        // and -Infinity minus itself is NaN, which breaks the sort.
        const timeA = getActivityTime(a);
        const timeB = getActivityTime(b);
        if (timeA === timeB) {
          return 0;
        }
        return timeA < timeB ? -1 : 1;
      }
      default:
        return 0;
    }
  };

  return [...rows].sort((a, b) =>
    direction === "desc" ? compare(b, a) : compare(a, b),
  );
};
